import React, { useEffect, useState } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useI18n } from './hooks/useI18n'
import { useCare } from './hooks/useCare'
import { useInviteLink } from './hooks/useInviteLink'
import { ToastProvider } from './contexts/ToastContext'

// Components
import LoadingScreen from './components/layout/LoadingScreen'
import AuthContainer from './components/auth/AuthContainer'
import MainLayout from './components/layout/MainLayout'
import HomePage from './components/pages/HomePage'
import MedicationsPage from './components/pages/MedicationsPage'
import CalendarPage from './components/pages/CalendarPage'
import ProgressPage from './components/pages/ProgressPage'
import ProfilePage from './components/pages/ProfilePage'
import CaseSelector from './components/care/CaseSelector'
import OnboardingFlow from './components/care/OnboardingFlow'
import UserManagement from './components/care/UserManagement'
import PricingPage from './components/subscription/PricingPage'
import PaymentResultPage from './components/subscription/PaymentResultPage'
// PWA Update handler
import PWAUpdateNotification from './components/layout/PWAUpdateNotification'
// Debug component
import InviteFlowDebug from './components/debug/InviteFlowDebug'
import LiveLogs from './components/debug/LiveLogs'

// Firebase imports para auto-join
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore'
import { db } from './config/firebase'
import { getDefaultPermissions } from './types/user'

function App() {
  const { user, loading } = useAuth()
  const { updateUI } = useI18n()
  const { currentCase, loading: careLoading, loadUserCases } = useCare()
  const { inviteCode, inviteType, isFromUrl, clearInvite } = useInviteLink()
  const [processingInvite, setProcessingInvite] = useState(false)
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false)

  // Update UI when component mounts
  React.useEffect(() => {
    updateUI()
  }, [updateUI])

  // Auto-join logic: cuando hay usuario autenticado + código de invitación + no hay casos
  useEffect(() => {
    const autoJoinWithInviteCode = async () => {
      // Debug logging
      console.log('🔍 Auto-join check:', JSON.stringify({
        userUid: user?.uid || 'NO_USER',
        inviteCode: inviteCode || 'NO_CODE',
        isFromUrl,
        currentCase: !!currentCase,
        currentCaseId: currentCase?.id || 'NO_CASE',
        careLoading,
        processingInvite
      }, null, 2))

      if (!user?.uid || !inviteCode || !isFromUrl || currentCase || careLoading || processingInvite || autoJoinAttempted) {
        console.log('❌ Auto-join blocked by conditions:', JSON.stringify({
          hasUser: !!user?.uid,
          userUid: user?.uid || 'NO_USER',
          hasInviteCode: !!inviteCode,
          inviteCode: inviteCode || 'NO_CODE',
          isFromUrl,
          hasCurrentCase: !!currentCase,
          currentCaseId: currentCase?.id || 'NO_CASE',
          careLoading,
          processingInvite,
          autoJoinAttempted
        }, null, 2))
        return
      }

      console.log('✅ Auto-join conditions met! Starting process...')
      setAutoJoinAttempted(true)

      // Pequeño delay para asegurar que todo esté listo
      await new Promise(resolve => setTimeout(resolve, 500))

      console.log('🔄 Procesando auto-join con código de invitación:', inviteCode)
      setProcessingInvite(true)

      try {
        // 1. Buscar el caso por código de invitación (buscar tanto en inviteCode como en profileInviteCode)
        let casesQuery
        const inviteTypeActual = inviteType || 'caregiver'

        if (inviteTypeActual === 'assisted') {
          // Buscar por código de invitación del asistido
          casesQuery = query(
            collection(db, 'care-cases'),
            where('profileInviteCode', '==', inviteCode),
            where('isActive', '==', true)
          )
        } else {
          // Buscar por código de invitación de cuidador
          casesQuery = query(
            collection(db, 'care-cases'),
            where('inviteCode', '==', inviteCode),
            where('isActive', '==', true)
          )
        }

        const casesSnapshot = await getDocs(casesQuery)

        if (casesSnapshot.empty) {
          console.error('❌ Código de invitación inválido:', inviteCode, 'tipo:', inviteTypeActual)
          clearInvite()
          setProcessingInvite(false)
          return
        }

        const caseDoc = casesSnapshot.docs[0]
        const caseData = caseDoc.data()

        console.log('✅ Caso encontrado:', caseData.name, 'tipo invitación:', inviteTypeActual)

        // 2. Verificar si el usuario ya es miembro
        const membersQuery = query(
          collection(db, 'case-members'),
          where('userId', '==', user.uid),
          where('caseId', '==', caseDoc.id),
          where('isActive', '==', true)
        )

        const membersSnapshot = await getDocs(membersQuery)

        if (!membersSnapshot.empty) {
          console.log('✅ Usuario ya es miembro del caso')
          clearInvite()
          await loadUserCases()
          setProcessingInvite(false)
          return
        }

        // 3. Determinar el rol y nickname según el tipo de invitación
        let role: 'caregiver' | 'care_recipient' = 'caregiver'
        let nickname = user.displayName || user.email?.split('@')[0] || 'Nuevo Cuidador'

        if (inviteTypeActual === 'assisted') {
          role = 'care_recipient'
          nickname = caseData.profileInfo?.name || 'Persona Asistida'

          // Verificar si ya hay un asistido en el caso
          const assistedQuery = query(
            collection(db, 'case-members'),
            where('caseId', '==', caseDoc.id),
            where('role', '==', 'care_recipient'),
            where('isActive', '==', true)
          )

          const assistedSnapshot = await getDocs(assistedQuery)

          if (!assistedSnapshot.empty) {
            console.error('❌ Ya hay una persona asistida en este caso')
            clearInvite()
            setProcessingInvite(false)
            return
          }

          // Marcar el perfil como reclamado
          await updateDoc(doc(db, 'care-cases', caseDoc.id), {
            profileUserId: user.uid,
            profileInviteCode: null // Invalidar el código después de usar
          })
        }

        // 4. Agregar al usuario como miembro del caso
        const membershipData = {
          userId: user.uid,
          caseId: caseDoc.id,
          role: role,
          permissions: getDefaultPermissions(role),
          joinedAt: serverTimestamp(),
          invitedBy: caseData.adminId,
          isActive: true,
          nickname: nickname,
          notes: `Se unió automáticamente usando código de invitación tipo ${inviteTypeActual}: ${inviteCode}`
        }

        await addDoc(collection(db, 'case-members'), membershipData)

        console.log('✅ Usuario añadido como miembro del caso con rol:', role)

        // 5. Limpiar el código de invitación y recargar casos
        clearInvite()
        setAutoJoinAttempted(false) // Reset para futuras invitaciones
        await loadUserCases()

        console.log('🎉 Auto-join completado exitosamente')

      } catch (error) {
        console.error('❌ Error en auto-join:', error)
        clearInvite()
        setAutoJoinAttempted(false) // Reset en caso de error
      } finally {
        setProcessingInvite(false)
      }
    }

    autoJoinWithInviteCode()
  }, [user?.uid, inviteCode, isFromUrl, currentCase, careLoading, processingInvite, autoJoinAttempted])

  if (loading) {
    return (
      <>
        {/* Debug components - only show in development */}
        {(import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true') && (
          <>
            <InviteFlowDebug
              autoJoinAttempted={autoJoinAttempted}
              resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
            />
            <LiveLogs />
          </>
        )}
        <LoadingScreen message="Iniciando sesión..." subMessage="Configurando tu cuenta" />
      </>
    )
  }

  if (careLoading) {
    return (
      <>
        {/* Debug components - only show in development */}
        {(import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true') && (
          <>
            <InviteFlowDebug
              autoJoinAttempted={autoJoinAttempted}
              resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
            />
            <LiveLogs />
          </>
        )}
        <LoadingScreen message="Cargando casos..." subMessage="Preparando tu información de cuidado" />
      </>
    )
  }

  if (processingInvite) {
    return (
      <>
        {/* Debug components - only show in development */}
        {(import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true') && (
          <>
            <InviteFlowDebug
              autoJoinAttempted={autoJoinAttempted}
              resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
            />
            <LiveLogs />
          </>
        )}
        <LoadingScreen
          message="¡Uniéndote al caso de cuidado!"
          subMessage="Procesando tu invitación automáticamente..."
        />
      </>
    )
  }

  if (!user) {
    return (
      <>
        {/* Debug components - only show in development */}
        {(import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true') && (
          <>
            <InviteFlowDebug
              autoJoinAttempted={autoJoinAttempted}
              resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
            />
            <LiveLogs />
          </>
        )}
        <AuthContainer />
      </>
    )
  }

  // Show loading screen if there's an invite code pending
  if (inviteCode && isFromUrl && !processingInvite) {
    console.log('🔄 Showing invite loading screen for code:', inviteCode)
    return (
      <>
        {/* Debug components - only show in development */}
        {(import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true') && (
          <>
            <InviteFlowDebug
              autoJoinAttempted={autoJoinAttempted}
              resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
            />
            <LiveLogs />
          </>
        )}
        <LoadingScreen
          message="¡Preparando tu acceso al caso!"
          subMessage="Validando tu invitación automáticamente..."
        />
      </>
    )
  }

  // Show onboarding flow if no case is selected (and not processing invite or invite code)
  if (!currentCase && !processingInvite && !inviteCode) {
    console.log('🔄 Showing onboarding flow - no case, no invite')
    return (
      <ToastProvider>
        {/* Debug components - only show in development */}
        {(import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true') && (
          <>
            <InviteFlowDebug
              autoJoinAttempted={autoJoinAttempted}
              resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
            />
            <LiveLogs />
          </>
        )}
        <OnboardingFlow />
      </ToastProvider>
    )
  }

  return (
    <ToastProvider>
      <InviteFlowDebug
        autoJoinAttempted={autoJoinAttempted}
        resetAutoJoinAttempted={() => setAutoJoinAttempted(false)}
      />
      <LiveLogs />
      <MainLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/medications" element={<MedicationsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/invite/:code" element={<OnboardingFlow />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payment/success" element={<PaymentResultPage />} />
          <Route path="/payment/cancel" element={<PaymentResultPage />} />
          <Route path="/payment/failure" element={<PaymentResultPage />} />
        </Routes>
        <PWAUpdateNotification />
      </MainLayout>
    </ToastProvider>
  )
}

export default App 